import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { replaceDate } from "@/helper/dateHandling";

export type RecordMetadataField =
  | "id"
  | "created_on"
  | "created_by"
  | "modified_on"
  | "modified_by";

const ALL_RECORD_METADATA_FIELDS: RecordMetadataField[] = [
  "id",
  "created_on",
  "created_by",
  "modified_on",
  "modified_by",
];

type Translate = (key: string) => string;

const buildIdColumn = (t: Translate): CrudColumnDef => ({
  header: t("id"),
  accessorKey: "id",
  id: "id",
});

const buildTimestampColumn = (
  field: "created_on" | "modified_on",
  t: Translate
): CrudColumnDef => ({
  header: t(field),
  accessorKey: field,
  id: field,
  filterVariant: "date",
  sortingFn: "datetime",
  Cell: ({ cell }) => (
    <span>{replaceDate(cell.getValue(), true, true)}</span>
  ),
});

const buildUserColumn = (
  field: "created_by" | "modified_by",
  t: Translate
): CrudColumnDef => ({
  header: t(field),
  id: field,
  accessorFn: (row) => row[field]?.name ?? "", 
  customColumnFilterId: `${field}_name`,
});

const buildRecordMetadataColumn = (
  field: RecordMetadataField,
  t: Translate
): CrudColumnDef => {
  switch (field) {
    case "id":
      return buildIdColumn(t);
    case "created_on":
    case "modified_on":
      return buildTimestampColumn(field, t);
    case "created_by":
    case "modified_by":
      return buildUserColumn(field, t);
  }
};

export const getRecordMetadataColumns = (
  t: Translate,
  fields: RecordMetadataField[] = ALL_RECORD_METADATA_FIELDS
): CrudColumnDef[] =>
  fields.map((field) => ({
    ...buildRecordMetadataColumn(field, t),
    hiddenByDefault: true,
    enableEditing: false,
  }));
